'use client';

import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,  } from 'recharts';

// Backend integration point: GET /api/ehs/metrics/trir-trend?range=12m&site=all
const trirData = [
  { month: 'May 25', trir: 2.1, dart: 1.2, benchmark: 1.8 },
  { month: 'Jun 25', trir: 1.9, dart: 1.1, benchmark: 1.8 },
  { month: 'Jul 25', trir: 2.3, dart: 1.4, benchmark: 1.8 },
  { month: 'Aug 25', trir: 2.0, dart: 1.2, benchmark: 1.8 },
  { month: 'Sep 25', trir: 1.7, dart: 0.9, benchmark: 1.8 },
  { month: 'Oct 25', trir: 1.8, dart: 1.0, benchmark: 1.8 },
  { month: 'Nov 25', trir: 1.6, dart: 0.95, benchmark: 1.8 },
  { month: 'Dec 25', trir: 1.9, dart: 1.1, benchmark: 1.8 },
  { month: 'Jan 26', trir: 1.75, dart: 1.0, benchmark: 1.8 },
  { month: 'Feb 26', trir: 1.55, dart: 0.88, benchmark: 1.8 },
  { month: 'Mar 26', trir: 1.60, dart: 0.92, benchmark: 1.8 },
  { month: 'Apr 26', trir: 1.42, dart: 0.86, benchmark: 1.8 },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg px-4 py-3 shadow-xl text-xs">
      <p className="font-600 text-[hsl(var(--foreground))] mb-2">{label}</p>
      {payload.map((entry: any) => (
        <div key={`tt-${entry.dataKey}`} className="flex items-center gap-2 mb-1">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-[hsl(var(--muted-foreground))]">{entry.name}:</span>
          <span className="font-600 tabular-nums text-[hsl(var(--foreground))]">{entry.value.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
};

export default function TRIRTrendChart() {
  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">TRIR & DART Rate Trend</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            12-month rolling · per 200,000 hours worked
          </p>
        </div>
        <span className="text-[11px] font-500 px-2 py-1 rounded-md bg-[hsl(var(--positive)/0.12)] text-[hsl(var(--positive))]">
          Below Benchmark ↓
        </span>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={trirData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="trirGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="dartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" strokeOpacity={0.6} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            domain={[0, 3]}
            tickCount={4}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine
            y={1.8}
            stroke="#ef4444"
            strokeDasharray="4 4"
            strokeWidth={1.5}
            label={{ value: 'BLS Benchmark 1.8', position: 'right', fontSize: 10, fill: '#ef4444' }}
          />
          <Area
            type="monotone"
            dataKey="trir"
            name="TRIR"
            stroke="#f59e0b"
            strokeWidth={2}
            fill="url(#trirGrad)"
            dot={false}
            activeDot={{ r: 4, fill: '#f59e0b' }}
          />
          <Area
            type="monotone"
            dataKey="dart"
            name="DART Rate"
            stroke="#8b5cf6"
            strokeWidth={2}
            fill="url(#dartGrad)"
            dot={false}
            activeDot={{ r: 4, fill: '#8b5cf6' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}